// ============================================================================
// Claude Code Thunk — WebSocket ↔ Redux Bridge
//
// Handles SDK messages from claude-agent-server:
//   - sdk_message with type "assistant" → text blocks + tool_use blocks
//   - sdk_message with type "result" → tool results
//   - error → error display
//
// The Claude Agent SDK sends content as an array of blocks:
//   [{ type: "text", text: "..." }, { type: "tool_use", id, name, input }, ...]
//
// Each assistant message may contain both text AND tool_use blocks.
// Tool results come as separate messages with type "result".
// ============================================================================
import { ClaudeCodeClient } from "./claudeCodeClient";
import { setConnectionStatus, setStreamingMessageKey, setCurrentToolCall, setClaudeCodeError, selectClaudeCodeSessionId, selectClaudeCodeStreamingMessageKey, selectCurrentToolCall } from "./claudeCodeSlice";
import { addMessageConversation, updateMessage, getConversation } from "containers/ChatGPT/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
// ---------------------------------------------------------------------------
// Singleton client reference
// ---------------------------------------------------------------------------
let client = null;
export const getClaudeCodeClient = () => client;
// ---------------------------------------------------------------------------
// Connect to the Claude Code agent server
// ---------------------------------------------------------------------------
export const connectToClaudeCode = (url, model, apiKey) => (dispatch, getState) => {
    if (client) {
        client.disconnect();
        client = null;
    }
    client = new ClaudeCodeClient({
        url,
        model,
        apiKey: apiKey || undefined,
        autoReconnect: true,
        onStatusChange: (status) => {
            dispatch(setConnectionStatus(status));
            if (status === "connected") {
                dispatch(setClaudeCodeError(null));
            }
            if (status === "error") {
                dispatch(setClaudeCodeError("Failed to connect to Claude Code server"));
            }
        },
        onMessage: (message) => {
            handleInboundMessage(message, dispatch, getState);
        }
    });
    client.connect();
};
// ---------------------------------------------------------------------------
// Disconnect
// ---------------------------------------------------------------------------
export const disconnectFromClaudeCode = () => (dispatch) => {
    if (client) {
        client.disconnect();
        client = null;
    }
    dispatch(setConnectionStatus("disconnected"));
};
// ---------------------------------------------------------------------------
// Send a message
// ---------------------------------------------------------------------------
export const sendClaudeCodeMessage = (text) => async (dispatch, getState) => {
    if (!client?.isConnected) {
        dispatch(addMessageConversation({
            user: "Aible",
            text: "Not connected to Claude Code server. Please check your connection.",
            date: new Date()
        }));
        return;
    }
    const sessionId = selectClaudeCodeSessionId(getState());
    // Clear stale tool call
    dispatch(setCurrentToolCall(null));
    // Add the user's message
    dispatch(addMessageConversation({
        user: "User",
        text,
        date: new Date()
    }));
    // Add streaming placeholder
    const assistantKey = dispatch(addMessageConversation({
        user: "Aible",
        text: "",
        date: new Date(),
        isStreaming: true,
        toolCall: null
    }));
    dispatch(setStreamingMessageKey(String(assistantKey)));
    dispatch(chatGPTReducer.updateIsAskingAgent(true));
    // Send to server
    client.sendMessage(text, sessionId);
};
// ---------------------------------------------------------------------------
// Interrupt (abort)
// ---------------------------------------------------------------------------
export const interruptClaudeCode = () => (dispatch, getState) => {
    if (!client?.isConnected)
        return;
    client.interrupt();
    const streamingKey = selectClaudeCodeStreamingMessageKey(getState());
    if (streamingKey) {
        const conversation = getConversation(getState());
        const currentMsg = conversation?.[streamingKey];
        if (currentMsg) {
            dispatch(updateMessage(streamingKey, {
                ...currentMsg,
                isStreaming: false
            }));
        }
    }
    dispatch(setStreamingMessageKey(null));
    dispatch(chatGPTReducer.updateIsAskingAgent(false));
};
// ---------------------------------------------------------------------------
// Inbound message handler
// ---------------------------------------------------------------------------
function handleInboundMessage(message, dispatch, getState) {
    switch (message.type) {
        case "connected":
            // Already handled by onStatusChange
            break;
        case "sdk_message":
            handleSdkMessage(message, dispatch, getState);
            break;
        case "error":
            handleErrorMessage(message.error, dispatch, getState);
            break;
    }
}
// ---------------------------------------------------------------------------
// SDK message handler
// ---------------------------------------------------------------------------
function handleSdkMessage(message, dispatch, getState) {
    const data = message.data;
    if (!data)
        return;
    switch (data.type) {
        case "assistant":
            handleAssistantMessage(data, dispatch, getState);
            break;
        case "result":
            handleToolResult(data, dispatch, getState);
            break;
        case "system":
            // System messages — could log or ignore
            console.log("[ClaudeCode] System message:", data);
            break;
    }
}
/**
 * Handle assistant messages.
 * Content is an array of blocks: text, tool_use, thinking, etc.
 * We extract text blocks for display and tool_use blocks for ToolCallDisplay.
 */
function handleAssistantMessage(data, dispatch, getState) {
    const content = data.message?.content;
    if (!content)
        return;
    const blocks = typeof content === "string" ? [{ type: "text", text: content }] : content;
    // Extract text
    const textParts = blocks
        .filter((b) => b.type === "text" && !!b.text)
        .map((b) => b.text);
    const fullText = textParts.join("\n");
    // Extract tool_use blocks
    const toolUseBlocks = blocks.filter((b) => b.type === "tool_use");
    // If there's a tool_use block, track it
    if (toolUseBlocks.length > 0) {
        const tool = toolUseBlocks[0]; // Take the first one
        const toolCall = {
            name: tool.name,
            input: tool.input,
            status: "running"
        };
        dispatch(setCurrentToolCall(toolCall));
    }
    const streamingKey = selectClaudeCodeStreamingMessageKey(getState());
    const toolCall = selectCurrentToolCall(getState());
    const stopReason = data.message?.stop_reason;
    const isDone = stopReason === "end_turn" || stopReason === "stop_sequence";
    if (!streamingKey) {
        // No streaming message — create one
        const newKey = dispatch(addMessageConversation({
            user: "Aible",
            text: fullText,
            date: new Date(),
            isStreaming: !isDone,
            ...(toolCall ? { toolCall } : { toolCall: null })
        }));
        dispatch(setStreamingMessageKey(String(newKey)));
        if (!isDone) {
            dispatch(chatGPTReducer.updateIsAskingAgent(true));
        }
    }
    else {
        // Update existing streaming message
        const conversation = getConversation(getState());
        const currentMsg = conversation?.[streamingKey];
        if (currentMsg) {
            dispatch(updateMessage(streamingKey, {
                ...currentMsg,
                text: fullText || currentMsg.text || "",
                isStreaming: !isDone,
                ...(toolCall ? { toolCall } : {})
            }));
        }
    }
    // If this is the final message (end_turn), clean up
    if (isDone) {
        dispatch(setCurrentToolCall(null));
        dispatch(setStreamingMessageKey(null));
        dispatch(chatGPTReducer.updateIsAskingAgent(false));
    }
}
/**
 * Handle tool results — update the tool call status to complete.
 */
function handleToolResult(data, dispatch, getState) {
    const output = data.output ?? "";
    const isError = data.is_error ?? false;
    const currentToolCall = selectCurrentToolCall(getState());
    if (currentToolCall) {
        const updatedToolCall = {
            ...currentToolCall,
            output,
            status: isError ? "error" : "complete"
        };
        dispatch(setCurrentToolCall(updatedToolCall));
        // Update the message too
        const streamingKey = selectClaudeCodeStreamingMessageKey(getState());
        if (streamingKey) {
            const conversation = getConversation(getState());
            const currentMsg = conversation?.[streamingKey];
            if (currentMsg) {
                dispatch(updateMessage(streamingKey, {
                    ...currentMsg,
                    toolCall: updatedToolCall
                }));
            }
        }
    }
}
/**
 * Handle error messages.
 */
function handleErrorMessage(errorText, dispatch, getState) {
    const streamingKey = selectClaudeCodeStreamingMessageKey(getState());
    if (streamingKey) {
        const conversation = getConversation(getState());
        const currentMsg = conversation?.[streamingKey];
        if (currentMsg) {
            dispatch(updateMessage(streamingKey, {
                ...currentMsg,
                text: `Error: ${errorText}`,
                isStreaming: false
            }));
        }
    }
    else {
        dispatch(addMessageConversation({
            user: "Aible",
            text: `Error: ${errorText}`,
            date: new Date()
        }));
    }
    dispatch(setStreamingMessageKey(null));
    dispatch(chatGPTReducer.updateIsAskingAgent(false));
}
